import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Copy } from "../../../assets/svg/copy.svg";
import { SPACING } from "../../../constants/design/spacing";
import { copyStringToClipboard, shortenString } from "../../../utils/strings";
import { ViewDetailsRow } from "./style";

interface ICopyRowProps {
  label: string;
  /** Full value. The row hides itself when this is empty. */
  value?: string;
  startLen?: number;
  endLen?: number;
  /** Block-explorer URL for the value. Adds a focusable "open" link. */
  href?: string;
  /** Accessible name for that link, e.g. "View transaction on explorer". */
  hrefLabel?: string;
}

/**
 * Detail row whose value is shortened (`0x1234...abcd`) and copied in full on
 * click — the same treatment the Trade ID / Order ID rows already get.
 */
export function CopyRow({
  label,
  value,
  startLen = 6,
  endLen = 4,
  href,
  hrefLabel,
}: ICopyRowProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "shared.ViewDetailsModal",
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const onCopy = useCallback(() => {
    if (!value) return;
    copyStringToClipboard(value);
    setCopied(true);
  }, [value]);

  if (!value) return null;

  return (
    <ViewDetailsRow clickable onClick={onCopy}>
      <span>{label}</span>
      <span>
        <Copy style={{ marginRight: SPACING.two }} />
        {copied
          ? t("copied", { defaultValue: "Copied" })
          : shortenString(value, startLen, endLen)}
        {href ? (
          // Its own control: the row still copies, this opens the explorer.
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={hrefLabel || t("view_on_explorer", { defaultValue: "View on explorer" })}
            title={hrefLabel || t("view_on_explorer", { defaultValue: "View on explorer" })}
            onClick={(e) => e.stopPropagation()}
            style={{ marginLeft: SPACING.two }}
          >
            ↗
          </a>
        ) : null}
      </span>
    </ViewDetailsRow>
  );
}

export default CopyRow;
